import { LanguageService } from '@web_opxp/features';
import { createBrowserHistory, UnregisterCallback } from 'history';
import { sessionStorage } from 'web_core_library';
import ENV from './env';

const history = createBrowserHistory({
  basename: ENV.BasePath,
});

// listen for location change
export function addListener(listener: (path: string) => void): UnregisterCallback {
  const currentPath = history.createHref(history.location);
  listener(currentPath);
  return history.listen((location) => {
    const path = history.createHref(location);
    listener(path);
  });
}

export const getNormalizedUrl = (url: URL) => {
  let normalizedUrl = url.toString();
  if (normalizedUrl.indexOf('/?') < 0 && normalizedUrl.indexOf('?') >= 0) {
    normalizedUrl = normalizedUrl.replace('?', '/?');
  }
  return normalizedUrl;
};

export const getExternalUrl = (url: string, lang?: string, session?: string) => {
  // get current origin
  const base = window.location.origin;
  // base will be applied only if url is relative
  const resultUrl = new URL(url, base);

  if (lang) {
    resultUrl.searchParams.set('lang', lang);
  }
  if (session) {
    resultUrl.searchParams.set('session', session);
  }
  return getNormalizedUrl(resultUrl);
};

export const getInternalUrl = (path?: string) => {
  return history.createHref({ pathname: path });
};

export const redirectExternal = (url?: string, lang?: string, session?: string) => {
  if (!url) {
    return;
  }
  const redirectUrl = getExternalUrl(url, lang, session);
  return window.location.assign(redirectUrl);
};

let windowObjectReference: Window | null = null;
let previousURL: string;

export function openExternalUrl(url: string) {
  if (windowObjectReference === null || windowObjectReference.closed) {
    windowObjectReference = window.open(url, 'NNChildWindow');
  } else if (previousURL !== url) {
    windowObjectReference = window.open(url, 'NNChildWindow');
    if (!windowObjectReference) {
      return;
    }
    windowObjectReference.focus();
  } else {
    windowObjectReference.focus();
  }
  previousURL = url;
}

export function buildCheckoutUrl(productId: number, coupon?: string): string {
  const checkoutUrl = new URL(ENV.CheckoutUrl);

  // set selected productId
  checkoutUrl.searchParams.set('productId', productId.toString());

  // set coupon information
  if (coupon) {
    checkoutUrl.searchParams.set('coupon', coupon);
  } else {
    checkoutUrl.searchParams.set('noCoupon', '1');
  }

  // set base parameters
  checkoutUrl.searchParams.set('backUrl', getBackUrl());
  checkoutUrl.searchParams.set('forwardSession', '1');

  // set training url to go after complete
  checkoutUrl.searchParams.set('completeUrl', getBackUrl('/dashboard'));

  return checkoutUrl.toString();
}

export const redirectToCheckout = (productId: number, lang: string, session: string, coupon?: string) => {
  const checkoutUrl = buildCheckoutUrl(productId, coupon);
  return redirectExternal(checkoutUrl, lang, session);
};

export function buildManagementUrl(language?: string, session?: string): string {
  const manageUrl = `${ENV.SubscribeAppPath}/manage/`;
  return getExternalUrl(manageUrl, language, session);
}

export function redirectToManagement(language?: string, session?: string) {
  const manageUrl = `${ENV.SubscribeAppPath}/manage/`;
  return redirectExternal(manageUrl, language, session);
}

export const redirectToOnboarding = (language?: string, session?: string) =>
  redirectExternal(`${ENV.OnboardingAppPath}/assessment`, language, session);

export const getSubscribePageUrl = (language?: string, session?: string) =>
  getExternalUrl(ENV.SubscribeAppPath, language, session);

export const redirectToSubscribe = (language?: string, session?: string) =>
  redirectExternal(ENV.SubscribeAppPath, language, session);

export const redirectToSubscribeWithCoupon = (coupon?: string, language?: string, session?: string) =>
  redirectExternal(coupon ? `${ENV.SubscribeAppPath}?coupon=${coupon}` : ENV.SubscribeAppPath, language, session);

export const getUserPageUrl = (language?: string, session?: string) =>
  getExternalUrl(ENV.UserAppPath, language, session);

export const getUserPrivacySubpageUrl = (language?: string, session?: string) =>
  getExternalUrl(`${ENV.UserAppPath}/privacy`, language, session);

const getSocialUrl = (subpage = '', language?: string, session?: string) =>
  getExternalUrl(`${ENV.SocialAppPath}${subpage}`, language, session);

export const getFriendsUrl = (language?: string, session?: string) => getSocialUrl('/friends', language, session);

export const getMessagesUrl = (language?: string, session?: string) => getSocialUrl('/messages', language, session);

export const redirectInternal = (pathname: string, state = {}) => {
  // do not redirect if already on migration page
  if (history.location.pathname === pathname) {
    return;
  }
  return history.push({ pathname, state });
};

export const getBackUrl = (path?: string) => {
  // Don't change the following to ?? because path can be empty string
  const backUrlPath = path || history.location.pathname;
  const internalHref = getInternalUrl(backUrlPath);
  const backUrl = new URL(internalHref, window.location.origin);
  return getNormalizedUrl(backUrl);
};

export const getSsoRedirectUrl = (
  language?: string,
  signup = false,
  backUrlPath?: string,
  logout?: boolean,
  signin?: string
) => {
  const ssoService = logout ? 'logout' : 'opxp/login';
  const redirectUrl = new URL(`${ENV.AuthBaseUrl}/${ssoService}/?theme=opxp`);
  const backUrl = getBackUrl(backUrlPath);
  redirectUrl.searchParams.set('backUrl', backUrl);
  if (language) {
    redirectUrl.searchParams.set('lang', language);
  }

  if (logout) {
    redirectUrl.searchParams.set('logout', '1');
  } else if (signin) {
    redirectUrl.searchParams.set('signin', signin);
  }
  return getNormalizedUrl(redirectUrl);
};

export const redirectToSSO = (backUrlPath?: string, logout?: boolean, signin?: string, signup = false) => {
  const language = LanguageService.getLanguage() ?? 'de';
  const redirectUrl = getSsoRedirectUrl(language, signup, backUrlPath, logout, signin);
  sessionStorage.remove('session');
  return window.location.assign(redirectUrl);
};

export function redirectLogout(backUrlPath = '') {
  return redirectToSSO(backUrlPath, true);
}

export function exchangeAccessToken(accessToken: string) {
  return redirectToSSO('', false, accessToken);
}

export function redirectLogin(backUrlPath?: string) {
  return redirectToSSO(backUrlPath, false, '', true);
}

export const getLoginLink = (language: string, backUrlPath = '/') => getSsoRedirectUrl(language, false, backUrlPath);

export const getCurrentPath = () => history.location.pathname;

export const isOnPage = (pathname: string) => {
  return getCurrentPath().includes(pathname);
};

export const getZendeskUrl = (lang: string, session?: string) => {
  const locale = lang === 'en' ? 'en-us' : lang;
  const tokenParam = session ? `?signin=${session}` : '';
  return `${ENV.ApiUrl}/zendesk/${locale}${tokenParam}`;
};

export const getBlogUrl = (lang: string) => `${ENV.BlogPath}/${lang}`;

export const getEffectivenessUrl = (lang: string) => {
  const blogUrl = getBlogUrl(lang);
  let path = '';
  switch (lang) {
    case 'en':
      path = '/bulletproof-brain-training-study-proves-effectiveness-of-neuronation-premium/';
      break;
    case 'de':
      path = '/neue-studie-beweist-neuronation-mit-hoher-signifikanz-effektiv/';
      break;
    case 'fr':
      path = '/resultats-detude-neuronation-ameliore-la-memoire/';
      break;
    case 'ru':
      path =
        '/%d1%80%d0%b5%d0%b7%d1%83%d0%bb%d1%8c%d1%82%d0%b0%d1%82%d1%8b-%d0%b8%d1%81%d1%81%d0%bb%d0%b5%d0%b4%d0%be%d0%b2%d0%b0%d0%bd%d0%b8%d1%8f-neuronation-%d1%83%d0%bb%d1%83%d1%87%d1%88%d0%b0%d0%b5%d1%82/';
      break;
  }
  return `${blogUrl}${path}`;
};

export const getPrivacyPolicyUrl = (lang: string) => {
  const url = new URL(ENV.PrivacyPolicyUrl);
  url.searchParams.set('lang', lang);
  return url.toString();
};

export const getCurrentSearchParams = () => {
  const search = history.location.search;
  return new URLSearchParams(search);
};

export const updateSearchParams = (newSearch: URLSearchParams) => {
  const location = history.location;
  const newSearchQuery = `?${newSearch.toString()}`;
  if (location.search !== newSearchQuery) {
    location.search = newSearchQuery;
    history.replace(location);
  }
};

export const getMigrationUrl = (lang: string, session?: string) =>
  getExternalUrl(`${ENV.UserAppPath}/switch-back`, lang, session);

export const redirectMigration = (lang: string, session?: string) =>
  redirectExternal(`${ENV.UserAppPath}/switch-back`, lang, session);

export const refreshToken = () => {
  sessionStorage.remove('session');
  const backUrlPath = getCurrentPath();
  return redirectLogin(backUrlPath);
};

export const getMedRecruitmentLink = () => {
  const medUrl = new URL(
    'https://neuronation-med.de/?utm_source=neuronation&utm_medium=banner&utm_campaign=top-banner-dashboard'
  );
  // attribution can be added here if needed
  return medUrl.toString();
};

export const redirectToMedRecruitment = () => {
  return openExternalUrl(getMedRecruitmentLink());
};

export const getJobsUrl = () => {
  const jobsUrl = new URL('https://neuronation.breezy.hr/');
  return jobsUrl.toString();
};

export const getGroupPackageUrl = () => {
  const groupPackageUrl = getExternalUrl(`${ENV.OnboardingAppPath}/lp/corp`);
  return groupPackageUrl.toString();
};

export default history;
